// @flow
import _debug from 'debug';
import { isEmpty } from 'lodash';
import {
  createAsyncThunk,
  createSelector,
  createSlice
} from '@reduxjs/toolkit';
import getListing from '@justpark/api/src/requests/getListing';
import getRequestError from '@justpark/api/src/helpers/getRequestError';
import checkoutSummaryRequest from '@justpark/api/src/requests/checkoutSummary';
import getRequestErrorMessageCode from '@justpark/api/src/helpers/getRequestErrorMessageCode';
import getApiMetaErrors from '@justpark/helpers/src/getApiMetaErrors/getApiMetaErrors';
import createBooking3DS2Request from '@justpark/api/src/requests/create3DS2Booking';
import type { CheckoutSummary } from '@justpark/api/src/types';
import findIsPrimary from '@justpark/helpers/src/findIsPrimary/findIsPrimary';
import createPaymentMethod from '@justpark/api/src/requests/createPaymentMethod';
import type { PayloadAction } from '@reduxjs/toolkit';
import convertToDayjs from '@justpark/helpers/src/dates/convertToDayjs';
import sortBreakdown from '../helpers/sortBreakdown';
import { logout, selectUser, selectUserPhone } from './auth';
import { loadVehicles, selectVehicles } from './vehicle';
import type { Analytics, ChallengeResponse, CheckoutResponse } from '../types';
import {
  createPaymentMethod as createPaymentMethodAction,
  SELECT_PAYMENTMETHOD,
  selectActivePaymentMethodId,
  selectPaymentDetailsLocked
} from './payments';
import { client } from '../api/Api';

import { makeActionCreator } from '../helpers/utils';

const debug = _debug('jp:reducers:monthlyCheckout');

const INIT_EXPONEA_CHECKOUT_EVENT = 'checkout/INIT_EXPONEA_CHECKOUT_EVENT';

export type State = {|
  loadingListing: boolean,
  listingLoaded: boolean,
  listing: {
    excludedPaymentMethods: []
  },
  loadingCheckoutSummary: boolean,
  checkoutSummaryLoaded: boolean,
  checkoutSummary: ?CheckoutSummary,
  apiErrors: {
    availability: string,
    title: string,
    code: string
  },
  isSummaryApiInvoked: boolean,
  showLogin: boolean,
  showRegistrationForm: boolean,
  personalDetailsLocked: boolean,
  vehiclesLocked: boolean,
  personalDetailsAutoLockComplete: boolean,
  checkingOut: boolean,
  phoneNumber: string,
  vehicleId: number | string,
  callingCode: string,
  quoteId: string,
  ddcRequired: boolean,
  challengeResponse: null | ChallengeResponse,
  ddcErrorCode: string,
  ddcErrorMessage: string,
  bookingResponse: ?CheckoutResponse,
  hasChangedOptionalExtras: boolean,
  optionalExtras: {
    [string]: boolean
  }
|};

export const initialState: State = {
  loadingListing: false,
  listingLoaded: false,
  listing: {
    excludedPaymentMethods: []
  },
  loadingCheckoutSummary: false,
  checkoutSummaryLoaded: false,
  checkoutSummary: null,
  apiErrors: {
    availability: '',
    title: '',
    code: ''
  },
  isSummaryApiInvoked: false,
  showLogin: false,
  showRegistrationForm: false,
  personalDetailsLocked: false,
  vehiclesLocked: false,
  personalDetailsAutoLockComplete: false,
  checkingOut: false,
  phoneNumber: '',
  callingCode: '',
  quoteId: '',
  vehicleId: '',
  ddcRequired: false,
  challengeResponse: null,
  ddcErrorCode: '',
  ddcErrorMessage: '',
  bookingResponse: null,
  hasChangedOptionalExtras: false,
  optionalExtras: {}
};

const checkoutPartners = ['zapmap', 'parkopedia'];

const sliceName = 'monthlyCheckout';

export const setExponeaCheckoutEvent = makeActionCreator(
  INIT_EXPONEA_CHECKOUT_EVENT
);

export const loadListing = createAsyncThunk(
  'checkout/loadListing',
  async (listingId, { rejectWithValue, extra: { jpApiClient } }) => {
    try {
      const { data } = await jpApiClient(
        getListing({ listingId, useV4: true, isJp: true })
      );
      return data;
    } catch (e) {
      return rejectWithValue(getRequestError(e));
    }
  }
);

export const postCheckoutSummary = createAsyncThunk(
  'checkout/postCheckoutSummary',
  async (payload, { rejectWithValue, extra: { jpApiClient } }) => {
    try {
      const { data } = await jpApiClient(
        checkoutSummaryRequest({ ...payload, isJp: true })
      );
      return data;
    } catch (e) {
      return rejectWithValue(getRequestErrorMessageCode(e));
    }
  }
);

export const createMonthlyBooking = createAsyncThunk(
  'monthlyCheckout/createBooking',
  async (payload, { rejectWithValue, getState, extra: { jpApiClient } }) => {
    debug('postcreateMonthlyBooking', payload);
    const sessionId = payload?.sessionId;
    const paymentMethodIdArg = payload?.paymentMethodIdArg;
    const state = getState();
    const { monthlyCheckout, brand } = state;
    const { partner } = brand;
    const { quoteId, challengeResponse } = monthlyCheckout;
    const paymentMethodId = selectActivePaymentMethodId(state);
    const partnerName = checkoutPartners.includes(partner) ? partner : null;

    try {
      const request = createBooking3DS2Request(
        paymentMethodIdArg || paymentMethodId,
        quoteId,
        false,
        partnerName,
        sessionId
          ? {
              session_id: sessionId,
              challenge_window_size: 'fullPage',
              return_url: `${window.location.origin}/3ds-challenge-return`
            }
          : null,
        challengeResponse
      );

      const response = await jpApiClient(request);
      const { data } = response.data;

      const challengeReturned = !!data?.jwt;

      if (challengeReturned) {
        const err = new Error('3DS2 Challenge required');
        err.data = {
          ...data,
          challengedPaymentMethodId: paymentMethodIdArg || paymentMethodId
        };

        return rejectWithValue(err);
      }
      return { data };
    } catch (e) {
      const error = e?.response?.data || e;

      return rejectWithValue(error);
    }
  }
);

export const tokeniseAndDoBooking = createAsyncThunk(
  'monthlyCheckout/tokeniseAndDoBooking',
  async (payload, { rejectWithValue, dispatch, extra: { jpApiClient } }) => {
    const { paymentMethodData, isGooglePay, sessionId } = payload;

    const doBookingWithResponse = (response) => {
      const { id } = response.data;
      return dispatch(
        createMonthlyBooking({ paymentMethodIdArg: id, sessionId })
      );
    };

    let createPaymentMethodResponse = null;

    try {
      if (isGooglePay) {
        const { tokenizationData, info } = paymentMethodData;
        const { name, countryCode, postalCode } = info.billingAddress;
        const { token } = tokenizationData;
        const { signature, protocolVersion, signedMessage } = JSON.parse(token);

        await client.paymentMethods
          .tokenizeGooglePay(signature, protocolVersion, signedMessage)
          .then((tokenId) =>
            jpApiClient(
              createPaymentMethod(tokenId, name, countryCode, postalCode)
            )
          )
          .then((response) => {
            createPaymentMethodResponse = response.data;
          });
      } else {
        const { paymentData, paymentMethod } = paymentMethodData;
        await client.paymentMethods
          .tokenizeApplePay(paymentData, paymentMethod)
          .then((tokenId) => client.paymentMethods.createApplePay(tokenId))
          .then((response) => {
            createPaymentMethodResponse = response;
          });
      }
    } catch (error) {
      return rejectWithValue({
        message: isGooglePay ? 'Google Pay Error' : 'Apple Pay Error'
      });
    }

    return doBookingWithResponse(createPaymentMethodResponse).then((res) => {
      if (res?.meta?.requestStatus !== 'fulfilled') {
        throw new Error('Server side error');
      }
    });
  }
);

const extractAvailableOptionalExtrasFromResponse = (response) =>
  response.checkoutAddons?.map((item) => item.type) || [];

const extractOptionalExtrasFromResponse = (response) => {
  // Look at all our options, and check if they are in the price breakdown
  const optionsKeys = extractAvailableOptionalExtrasFromResponse(response);
  const breakdownKeys = response.price.breakdown.map((item) => item.key);

  return optionsKeys
    .map((optionKey) => ({
      [`${optionKey}`]: breakdownKeys.indexOf(optionKey) > -1
    }))
    .reduce(
      (acc, curr) => ({
        ...acc,
        ...curr
      }),
      {}
    );
};

const monthlyCheckoutSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    toggleLogin: (state): State => ({
      ...state,
      showLogin: !state.showLogin
    }),
    setShowRegistrationForm: (state, action: Action): State => ({
      ...state,
      showRegistrationForm: action.payload
    }),
    setPersonalDetailsLock: (state, action: Action): State => {
      const { payload } = action;
      return {
        ...state,
        personalDetailsLocked: payload
      };
    },
    setPersonalDetailsAutoLock: (state, action: Action): State => {
      const { payload } = action;
      return {
        ...state,
        personalDetailsLocked: payload,
        personalDetailsAutoLockComplete: payload
      };
    },
    setPhoneNumber: (state, action: Action): State => ({
      ...state,
      phoneNumber: action.payload
    }),
    setCallingCode: (state, action: Action): State => ({
      ...state,
      callingCode: action.payload
    }),
    setVehiclesLock: (state, action: Action): State => ({
      ...state,
      vehiclesLocked: action.payload
    }),
    setSelectedVehicleId: (state, action: Action): State => ({
      ...state,
      vehicleId: action.payload
    }),
    resetApiErrors: (state: State) => ({
      ...state,
      apiErrors: {
        availability: null,
        title: '',
        code: ''
      }
    }),
    resetMonthlyCheckout: () => ({
      ...initialState
    }),
    clearSensitiveData: (state: State) => ({
      ...initialState,
      listingLoaded: true,
      listing: state.listing
    }),
    startDDC: (state: State): State => ({
      ...state,
      ddcRequired: true,
      checkingOut: true,
      ddcErrorCode: '',
      ddcErrorMessage: ''
    }),
    onDDCFailure: (state: State, action): State => ({
      ...state,
      ddcErrorCode: action.payload.error,
      ddcErrorMessage: action.payload.message,
      checkingOut: false,
      ddcRequired: false
    }),
    createBookingFail: (state: State, action: PayloadAction) => {
      debug('createBookingFail', action.payload);

      return {
        ...state,
        checkingOut: false,
        globalApiError: {
          ...action.payload.data
        }
      };
    },
    changeOptionalExtra: (state: State, action): State => {
      debug('changeOptionalExtra', action.payload);

      return {
        ...state,
        hasChangedOptionalExtras: true,
        optionalExtras: {
          ...state.optionalExtras,
          [action.payload.key]: action.payload.value
        }
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadListing.pending, (state: State): State => {
      debug(loadListing.pending);
      return { ...state, loadingListing: true };
    });
    builder.addCase(
      loadListing.fulfilled,
      (state: State, action: PayloadAction): State => {
        debug(loadListing.fulfilled, action);
        return {
          ...state,
          loadingListing: false,
          listingLoaded: true,
          listing: action.payload.data
        };
      }
    );
    builder.addCase(createPaymentMethodAction.pending, (state) => ({
      ...state,
      ddcErrorCode: '',
      ddcErrorMessage: ''
    }));
    builder.addCase(SELECT_PAYMENTMETHOD, (state: State): State => ({
      ...state,
      ddcErrorCode: '',
      ddcErrorMessage: ''
    }));
    builder.addCase(
      loadListing.rejected,
      (state: State, action: PayloadAction): State => {
        debug(loadListing.rejected);
        const code = action.payload?.data?.code;
        const title = action.payload?.data?.title;

        return {
          ...state,
          loadingListing: false,
          listingLoaded: false,
          apiErrors: { code, title }
        };
      }
    );
    builder.addCase(postCheckoutSummary.pending, (state: State): State => {
      debug(postCheckoutSummary.pending);
      return { ...state, loadingCheckoutSummary: true };
    });
    builder.addCase(
      postCheckoutSummary.fulfilled,
      (state: State, action: PayloadAction): State => {
        debug(postCheckoutSummary.fulfilled, action);

        // If this is the first checkout summary response then use that data to work out the default state for the
        // SMS toggles. Save that data into it's own redux state to then allow us to toggle it on or off
        const isFirstOrNewCheckoutResponse =
          state.quoteId === '' || state.quoteId !== action.payload.data.quoteId;

        const { hasChangedOptionalExtras } = state;

        const optionalExtras =
          isFirstOrNewCheckoutResponse || !hasChangedOptionalExtras
            ? extractOptionalExtrasFromResponse(action.payload.data)
            : state.optionalExtras;

        return {
          ...state,
          loadingCheckoutSummary: false,
          checkoutSummaryLoaded: true,
          checkoutSummary: action.payload.data,
          quoteId: action.payload.data.quoteId,
          apiErrors: {
            ...state.apiErrors,
            availability: null
          },
          isSummaryApiInvoked: true,
          optionalExtras
        };
      }
    );
    builder.addCase(
      postCheckoutSummary.rejected,
      (state: State, action: PayloadAction): State => {
        debug(postCheckoutSummary.rejected, action);
        const meta = action.payload?.meta;
        const code = action?.payload?.code;

        // 5002 is a max stay error: meta is is incorrect format for FE
        const metaErrors =
          meta && code !== 5002 ? getApiMetaErrors(meta) : null;

        return {
          ...state,
          checkoutSummaryLoaded: false,
          loadingCheckoutSummary: false,
          checkoutSummary: null,
          apiErrors: {
            ...state.apiErrors,
            availability: metaErrors || action?.payload?.message,
            code
          },
          isSummaryApiInvoked: true
        };
      }
    );
    builder.addCase(
      loadVehicles.fulfilled,
      (state: State, action: Action): State => {
        debug('loadVehicles.fulfilled', action);

        const vehicles = action.payload;

        return {
          ...state,
          vehiclesLocked: !isEmpty(vehicles),
          vehicleId: findIsPrimary(vehicles)?.id
        };
      }
    );
    builder.addCase(createMonthlyBooking.pending, (state: State) => ({
      ...state,
      checkingOut: true,
      ddcRequired: false,
      globalApiError: null
    }));
    builder.addCase(
      createMonthlyBooking.fulfilled,
      (state: State, action: Action) => ({
        ...state,
        checkingOut: false,
        bookingResponse: action.payload.data
      })
    );
    builder.addCase(
      createMonthlyBooking.rejected,
      (state: State, action: Action) => {
        if (action?.payload?.data?.jwt) {
          // 3DS2 Challenge response

          return {
            ...state,
            challengeResponse: action.payload.data
          };
        }
        const { error: code, meta } = action.payload;

        if (code === 2000) {
          const allValidationErrors = getApiMetaErrors(meta);

          return {
            ...state,
            checkingOut: false,
            challengeResponse: null,
            apiErrors: {
              title: allValidationErrors,
              code,
              availability: ''
            }
          };
        }
        // then this is an error associated with the entire booking process so we should modal it
        return {
          ...state,
          checkingOut: false,
          challengeResponse: null,
          apiErrors: {
            title: action.payload.message,
            code: action.payload.error,
            availability: ''
          }
        };
      }
    );
    builder.addCase(
      tokeniseAndDoBooking.rejected,
      (state: State, action: Action) => ({
        ...state,
        apiErrors: {
          title: action.payload?.message || state.apiErrors.title,
          code: action.payload?.code || state.apiErrors.code
        }
      })
    );
    builder.addCase(logout.fulfilled, (state: State) => ({
      ...initialState,
      listingLoaded: true,
      listing: state.listing
    }));
  }
});

export const {
  toggleLogin,
  setShowRegistrationForm,
  setPersonalDetailsLock,
  setPersonalDetailsAutoLock,
  setPhoneNumber,
  setCallingCode,
  setVehiclesLock,
  setSelectedVehicleId,
  resetApiErrors,
  startDDC,
  onDDCFailure,
  resetMonthlyCheckout,
  clearSensitiveData,
  createBookingFail,
  changeOptionalExtra
} = monthlyCheckoutSlice.actions;

const trackCheckout = (event, getState, analytics) => {
  const reducer = 'monthlyCheckout';

  const {
    listing: {
      id: listingId,
      acceptsPrebook,
      isManaged,
      title,
      category,
      lng,
      lat,
      formattedAddress,
      address,
      feedback,
      evCharging
    },
    monthlyDuration,
    startDate,
    endDate,
    promoCode,
    checkoutSummary
  } = getState()[reducer];

  const { user } = getState().auth;

  const { paymentMethods } = getState().payments;
  const activePaymentMethod = paymentMethods.find(
    (paymentMethod) => paymentMethod.isActive
  );
  const paymentMethod = activePaymentMethod ? activePaymentMethod.cardType : '';
  if (!acceptsPrebook) {
    return;
  }

  const bookingDurationType =
    monthlyDuration !== null ? 'MONTHLY' : 'HOURLY_DAILY';

  const trackingParams =
    monthlyDuration !== null
      ? { monthlyDuration }
      : {
          endDate: convertToDayjs(endDate).unix()
        };
  const city = address?.city;
  const country = address?.country;
  const averageRating = feedback?.averageRating;

  analytics.page(event, {
    listingId,
    startDate: convertToDayjs(startDate).unix(),
    bookingDurationType,
    platform: 'WEB',
    isManaged,
    phone: user?.phone || '',
    email: user?.email || '',
    paymentMethod,
    promoCode,
    listingAddress: formattedAddress,
    listingCategory: category,
    listingCity: city,
    listingCountry: country,
    listingLat: lat,
    listingLng: lng,
    listingName: title,
    listingRating: averageRating,
    isEV: evCharging,
    price: checkoutSummary?.price?.formatted || '',
    currency: checkoutSummary?.price?.currency || '',
    pennies: checkoutSummary?.price?.pennies || '',
    ...trackingParams
  });
};

const getOptionalExtrasAnalyticsState = (state) => {
  const availableOptionalExtras =
    state.monthlyCheckout.checkoutSummary?.checkoutAddons || [];
  const selectedOptionalExtras = state.monthlyCheckout.optionalExtras;

  if (availableOptionalExtras.length === 0) {
    return [];
  }

  return availableOptionalExtras.map(({ type, price }) => ({
    type,
    enabled: selectedOptionalExtras[type],
    price: price.value
  }));
};

export const monthlyCheckoutAnalyticsValueObservers = {
  optionalExtras: [
    (state) => state.monthlyCheckout.checkoutSummary?.quoteId,
    ({ state, analytics }) => {
      // A new quote ID means a new start checkout flow where we set optional extras
      const optionalExtras = getOptionalExtrasAnalyticsState(state);

      optionalExtras.forEach(({ type, price, enabled }) => {
        analytics.track(`${type}_available`, {
          enabled,
          price
        });
      });
    }
  ]
};

export const monthlyCheckoutAnalyticsEventSubscribers = {
  [INIT_EXPONEA_CHECKOUT_EVENT]: (action, analytics, getState) => {
    trackCheckout('checkout', getState, analytics);
  },
  [createMonthlyBooking.fulfilled]: (
    action: Action,
    analytics: Analytics,
    getState
  ) => {
    if (!(action.payload && action.payload.data)) {
      return;
    }

    const { driveUp } = action.payload.data;
    const {
      brand: { partner: partnerName }
    } = getState();

    analytics.track('action:payment:successful', {
      type: {
        driveUpPay: driveUp,
        preBook: !driveUp
      },
      isEv: false,
      partnerName
    });

    const optionalExtras = getOptionalExtrasAnalyticsState(getState());

    optionalExtras.forEach(({ type, price, enabled }) => {
      analytics.track(`${type}_purchased`, {
        enabled,
        price
      });
    });
  },
  [createMonthlyBooking.rejected]: (
    action: Action,
    analytics: Analytics,
    getState
  ) => {
    const {
      monthlyCheckout: { apiErrors },
      brand: { partner: partnerName }
    } = getState();
    const isEv = false;

    analytics.track('action:payment:failed', {
      isEv,
      partnerName,
      apiErrors
    });
  }
};

export const selectMonthlyCheckoutListing = (state) =>
  state.monthlyCheckout.listing;
export const selectMonthlyCheckoutListingId = (state) =>
  state.monthlyCheckout.listing?.id;
export const selectMonthlyCheckoutListingWhiteLabel = (state) =>
  state.monthlyCheckout.listing?.whiteLabel;
export const selectMonthlyCheckoutSummary = (state) =>
  state.monthlyCheckout.checkoutSummary;
export const selectMonthlyCheckoutSummaryLoaded = (state) =>
  state.monthlyCheckout.checkoutSummaryLoaded;
export const selectMonthlyPriceBreakdown = (state) =>
  state.monthlyCheckout.checkoutSummary?.price?.breakdown;
export const selectMonthlyCancellationPolicySummary = (state) =>
  state.monthlyCheckout.checkoutSummary?.cancellationPolicySummary || {};

export const selectMonthlyCheckoutPrice = (state) =>
  state.monthlyCheckout.checkoutSummary?.price;

const selectMonthlyCheckoutPayableNowPennies = (state) =>
  state.monthlyCheckout.checkoutSummary?.payableNow.pennies;

export const selectMonthlyCheckoutPaymentCash = (state) =>
  state.monthlyCheckout.checkoutSummary?.paymentAllocation?.cash;

const selectMonthlyCheckoutPaymentCashPennies = (state) =>
  state.monthlyCheckout.checkoutSummary?.paymentAllocation.cash.pennies;

export const selectMonthlyCheckoutPaymentCredit = (state) =>
  state.monthlyCheckout.checkoutSummary?.paymentAllocation.accountCredit;

const selectMonthlyCheckoutPaymentCreditPennies = (state) =>
  state.monthlyCheckout.checkoutSummary?.paymentAllocation.accountCredit
    .pennies;

export const selectMonthlyCheckoutPaymentPayFullWithCredit = createSelector(
  selectMonthlyCheckoutPayableNowPennies,
  selectMonthlyCheckoutPaymentCashPennies,
  selectMonthlyCheckoutPaymentCreditPennies,
  (payableNowPennies, cashPennies, creditPennies) =>
    !!(payableNowPennies === creditPennies && cashPennies === 0)
);

export const selectMonthlyCheckoutSortedBreakdown = createSelector(
  selectMonthlyPriceBreakdown,
  (breakdown) => breakdown && sortBreakdown(breakdown)
);

export const selectMonthlyCheckoutExcludedPaymentMethods = (state) =>
  state.monthlyCheckout.listing?.excludedPaymentMethods;
export const selectMonthlyCheckoutShowLogin = (state) =>
  state.monthlyCheckout.showLogin;
export const selectMonthlyCheckoutShowRegistrationForm = (state) =>
  state.monthlyCheckout.showRegistrationForm;
export const selectMonthlyCheckoutPersonalDetailsLocked = (state) =>
  state.monthlyCheckout.personalDetailsLocked;
export const selectMonthlyCheckoutVehiclesLocked = (state) =>
  state.monthlyCheckout.vehiclesLocked;
export const selectMonthlyCheckoutVehicleId = (state) =>
  state.monthlyCheckout.vehicleId;
export const selectMonthlyCheckoutPersonalDetailsAutoLockComplete = (state) =>
  state.monthlyCheckout.personalDetailsAutoLockComplete;
export const selectMonthlyCheckoutCheckingOut = (state) =>
  state.monthlyCheckout.checkingOut;
export const selectMonthlyCheckoutSummaryQuoteId = (state) =>
  state.monthlyCheckout.quoteId;

export const selectMonthlyCheckoutPhoneNumber = (state) =>
  state.monthlyCheckout.phoneNumber;
export const selectMonthlyCheckoutCallingCode = (state) =>
  state.monthlyCheckout.callingCode;
export const selectMonthlyCheckoutFullPhoneNumber = createSelector(
  selectMonthlyCheckoutPhoneNumber,
  selectMonthlyCheckoutCallingCode,
  (phoneNumber, callingCode) => `${callingCode}${phoneNumber}`
);

export const selectMonthlyCheckoutVehiclesDisabled = createSelector(
  selectUser,
  selectUserPhone,
  (user, userPhone) => !user || !userPhone
);

export const selectMonthlyCheckoutPaymentsDisabled = createSelector(
  selectMonthlyCheckoutVehiclesDisabled,
  selectMonthlyCheckoutVehicleId,
  (vehiclesDisabled, vehicleId) => vehiclesDisabled === true || !vehicleId
);

export const selectMonthlyCheckoutShowEditPhoneNumber = createSelector(
  selectUser,
  selectUserPhone,
  selectMonthlyCheckoutPersonalDetailsLocked,
  selectMonthlyCheckoutPersonalDetailsAutoLockComplete,
  (user, userPhone, personalDetailsLocked, personalDetailsAutoLockComplete) =>
    (user && userPhone === '') ||
    (personalDetailsAutoLockComplete && !personalDetailsLocked)
);

// While page is loading only render the Header until personal details has locked
export const selectMonthlyCheckoutVehiclesPaymentsRenderBody = createSelector(
  selectMonthlyCheckoutVehiclesDisabled,
  selectMonthlyCheckoutPersonalDetailsAutoLockComplete,
  (formDisabled, personalDetailsAutoLockComplete) =>
    !formDisabled && personalDetailsAutoLockComplete
);

export const selectMonthlyCheckoutGlobalErrorTitle = (state) =>
  state.monthlyCheckout.apiErrors.title;
export const selectMonthlyCheckoutAvailabilityError = (state) =>
  state.monthlyCheckout.apiErrors.availability;

export const selectMonthlyCheckoutGlobalErrorCode = (state) =>
  state.monthlyCheckout.apiErrors.code;

export const selectMonthlyCheckoutActiveVehicle = createSelector(
  selectMonthlyCheckoutVehicleId,
  selectVehicles,
  (selectedVehicleId, vehicles) =>
    vehicles.find(({ id }) => id === selectedVehicleId)
);

export const selectMonthlyCheckoutActiveVehicleVrm = createSelector(
  selectMonthlyCheckoutActiveVehicle,
  (activeMonthlyVehicle) => activeMonthlyVehicle?.registration || ''
);

export const selectMonthlyCheckoutDDCRequired = (state) =>
  state[monthlyCheckoutSlice.name].ddcRequired;

export const selectMonthlyCheckoutDDCErrorMessage = (state) =>
  state[monthlyCheckoutSlice.name].ddcErrorMessage;

export const selectMonthlyCheckoutDDCErrorCode = (state) =>
  state[monthlyCheckoutSlice.name].ddcErrorCode;

export const selectMonthlyCheckoutChallengeResponse = (state) =>
  state[monthlyCheckoutSlice.name].challengeResponse;

export const selectMonthlyCheckoutBookingResponse = (state) =>
  state[monthlyCheckoutSlice.name].bookingResponse;

export const selectMonthlyCheckoutBookingResponseStartDate = (state) =>
  state[monthlyCheckoutSlice.name].bookingResponse?.startDate;

export const selectMonthlyCheckoutBookingResponseEndDate = (state) =>
  state[monthlyCheckoutSlice.name].bookingResponse?.endDate;

export const selectCheckoutIsLoadingCheckoutSummary = (state) =>
  state[monthlyCheckoutSlice.name].loadingCheckoutSummary;

export const selectMonthlyCheckoutPayButtonDisabled = createSelector(
  selectMonthlyCheckoutCheckingOut,
  selectMonthlyCheckoutPaymentsDisabled,
  selectMonthlyCheckoutVehiclesLocked,
  selectPaymentDetailsLocked,
  selectCheckoutIsLoadingCheckoutSummary,
  selectMonthlyCheckoutDDCRequired,
  selectMonthlyCheckoutPaymentPayFullWithCredit,
  (
    submitting,
    paymentsDisabled,
    vehiclesLocked,
    paymentsLocked,
    loadingCheckoutSummary,
    ddcRequired,
    payFullWithCredit
  ) =>
    submitting ||
    paymentsDisabled ||
    vehiclesLocked === false ||
    (!payFullWithCredit && paymentsLocked === false) ||
    loadingCheckoutSummary ||
    ddcRequired
);

export const selectMonthlyCheckoutSubsequentPayments = (state) =>
  state[monthlyCheckoutSlice.name].checkoutSummary?.monthlyBooking
    ?.subsequentPayments || '';

export const selectMonthlyCheckoutUpgradedStartTime = (state) =>
  state[monthlyCheckoutSlice.name].checkoutSummary?.dates[0]
    .upgradedStartTime || null;

export const selectMonthlyCheckoutUpgradedEndTime = (state) =>
  state[monthlyCheckoutSlice.name].checkoutSummary?.dates[0].upgradedEndTime ||
  null;

export const selectMonthlyCheckoutOptionalExtras = (state) =>
  state[monthlyCheckoutSlice.name].checkoutSummary?.checkoutAddons;

export const selectMonthlyCheckoutOptionalExtraKeys = createSelector(
  selectMonthlyCheckoutOptionalExtras,
  (extras) => (extras ? extras.map((item) => item.type) : [])
);

export const selectMonthlyCheckoutSelectedOptionalPrice = (state) => {
  const breakdown =
    state[monthlyCheckoutSlice.name].checkoutSummary?.price.breakdown;

  if (!breakdown) {
    return '';
  }

  const currency =
    state[monthlyCheckoutSlice.name].checkoutSummary?.price.currency;

  const availableOptionalExtras = extractAvailableOptionalExtrasFromResponse(
    state[monthlyCheckoutSlice.name].checkoutSummary
  );

  const totalPennies = breakdown
    .filter(({ key }) => availableOptionalExtras.indexOf(key) > -1)
    .map(({ pennies }) => pennies)
    .reduce((acc, value) => acc + value, 0);

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency
  }).format(totalPennies / 100);
};

export const selectMonthlyCheckoutSelectedOptionalExtras = (state) =>
  state[monthlyCheckoutSlice.name].optionalExtras;

const selectMonthlyCheckoutHasChangedOptionalExtras = (state) =>
  state[monthlyCheckoutSlice.name].hasChangedOptionalExtras;

export const selectMonthlyCheckoutSelectedOptionalExtrasArray = createSelector(
  selectMonthlyCheckoutSelectedOptionalExtras,
  (data) =>
    Object.entries(data)
      .filter((entry) => entry[1])
      .map(([key]) => key)
);

export const selectMonthlyCheckoutChangedOptionalExtras = createSelector(
  selectMonthlyCheckoutHasChangedOptionalExtras,
  selectMonthlyCheckoutSelectedOptionalExtras,
  (hasChanged, data) => (hasChanged ? data : undefined)
);

export default monthlyCheckoutSlice;
